import { ArrowRight, ArrowUpRight, ExternalLink } from 'lucide-react';
import { cn } from '@/shared/lib/utils/classnames';
import type { LinkButtonBlock as LinkButtonBlockType } from '@/shared/types/blocks';
import { Button } from '@/shared/ui/button';

type LinkButtonBlockProps = Omit<LinkButtonBlockType, 'type' | 'id'>;

/** Gmail 버튼용 커스텀 화살표 아이콘 (Figma 기준) */
function GmailArrowIcon({ className }: { className?: string }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <title>Arrow</title>
      <mask
        id="mask-gmail-arrow"
        style={{ maskType: 'alpha' }}
        maskUnits="userSpaceOnUse"
        x="0"
        y="0"
        width="16"
        height="16"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M11.7239 8.66639L6.86195 13.5283L7.80476 14.4711L13.8048 8.47113L14.2762 7.99973L13.8048 7.52832L7.80476 1.52832L6.86195 2.47113L11.7239 7.33306H2V8.66639H11.7239Z"
          fill="black"
        />
      </mask>
      <g mask="url(#mask-gmail-arrow)">
        <rect width="16.0001" height="16" fill="currentColor" />
      </g>
    </svg>
  );
}

/** 더 보기 버튼용 chevron down 아이콘 (Figma 기준) */
function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <title>Chevron Down</title>
      <mask
        id="mask-chevron-down"
        style={{ maskType: 'alpha' }}
        maskUnits="userSpaceOnUse"
        x="0"
        y="0"
        width="16"
        height="16"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M7.99878 10.0569L2.47016 4.52832L1.52734 5.47113L7.52737 11.4711L7.99878 11.9425L8.47018 11.4711L14.4702 5.47113L13.5274 4.52832L7.99878 10.0569Z"
          fill="black"
        />
      </mask>
      <g mask="url(#mask-chevron-down)">
        <rect width="16.0001" height="16" fill="currentColor" />
      </g>
    </svg>
  );
}

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  'arrow-right': ArrowRight,
  'arrow-up-right': ArrowUpRight,
  'external-link': ExternalLink,
  'gmail-arrow': GmailArrowIcon,
  'chevron-down': ChevronDownIcon,
};

/**
 * LinkButtonBlock - 링크 버튼
 *
 * 외부 링크로 이동하는 버튼입니다.
 */
export function LinkButtonBlock({
  label,
  href,
  variant = 'default',
  size,
  icon,
  fullWidth = false,
  width,
}: LinkButtonBlockProps) {
  const IconComponent = icon ? ICON_MAP[icon] : null;

  const buttonContent = (
    <>
      {label}
      {/* Figma: 텍스트와 아이콘 사이 간격 6px */}
      {IconComponent && <IconComponent className="w-4 h-4 ml-1.5" />}
    </>
  );

  const widthStyle = width ? { width: `${width}px` } : undefined;

  // fullWidth가 아니면 왼쪽 정렬 (self-start)
  const alignmentClass = fullWidth ? 'w-full' : 'self-start';

  if (href) {
    return (
      <Button
        variant={variant}
        size={size}
        className={cn('justify-center cursor-pointer', alignmentClass)}
        style={widthStyle}
        asChild
      >
        <a href={href} target="_blank" rel="noopener noreferrer">
          {buttonContent}
        </a>
      </Button>
    );
  }

  return (
    <Button
      variant={variant}
      size={size}
      className={cn('justify-center cursor-pointer', alignmentClass)}
      style={widthStyle}
    >
      {buttonContent}
    </Button>
  );
}
